import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type { AuthMode } from "@/pages/auth/hooks/useAuthForm";
import { useAuthForm } from "@/pages/auth/hooks/useAuthForm";

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="mt-1.5 flex items-center gap-1 text-xs text-accent-700">
      <span className="flex h-3.5 w-3.5 items-center justify-center">
        <i className="ri-error-warning-line" />
      </span>
      {message}
    </p>
  );
}

export default function AuthCard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { mode, values, errors, submitting, formError, updateField, switchMode, submit } = useAuthForm();
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    setInfo(null);
  }, [mode]);

  const redirectTo = (() => {
    const target = searchParams.get("redirect");
    return target && target.startsWith("/") ? target : "/";
  })();

  const handleSubmit = async () => {
    const result = await submit();
    if (!result.ok) return;
    if (result.needsConfirm) {
      switchMode("login");
      setInfo("注册成功！我们已发送验证邮件，请查收并完成验证后再登录。");
      return;
    }
    navigate(redirectTo, { replace: true });
  };

  const inputClass = (hasError?: string) =>
    `w-full rounded-md border bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:ring-2 ${
      hasError
        ? "border-accent-400 focus:border-accent-500 focus:ring-accent-100"
        : "border-background-300 focus:border-primary-400 focus:ring-primary-100"
    }`;

  const tabs: { key: AuthMode; label: string }[] = [
    { key: "login", label: "登录" },
    { key: "register", label: "注册" },
  ];

  return (
    <div className="w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
      <div className="flex gap-1 rounded-full border border-background-200 bg-background-100 p-1">
        {tabs.map((tab) => {
          const active = tab.key === mode;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => switchMode(tab.key)}
              className={`flex-1 cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                active ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <h1 className="mt-6 font-heading text-xl font-semibold text-foreground-950">
        {mode === "login" ? "欢迎回来" : "创建你的账号"}
      </h1>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
        {mode === "login"
          ? "登录后即可申请授权、查看本人许可证与申请进度。"
          : "注册即可提交授权申请，并实时跟踪许可证状态。"}
      </p>

      <form
        className="mt-6 flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        {mode === "register" && (
          <div>
            <label htmlFor="fullName" className="mb-1.5 block text-sm font-medium text-foreground-800">
              姓名 / 昵称 <span className="text-accent-600">*</span>
            </label>
            <input
              id="fullName"
              name="fullName"
              type="text"
              autoComplete="name"
              value={values.fullName}
              onChange={(event) => updateField("fullName", event.target.value)}
              className={inputClass(errors.fullName)}
              placeholder="请输入姓名或昵称"
            />
            <FieldError message={errors.fullName} />
          </div>
        )}

        <div>
          <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
            邮箱 <span className="text-accent-600">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            value={values.email}
            onChange={(event) => updateField("email", event.target.value)}
            className={inputClass(errors.email)}
            placeholder="you@example.com"
          />
          <FieldError message={errors.email} />
        </div>

        {mode === "register" && (
          <div>
            <label htmlFor="company" className="mb-1.5 block text-sm font-medium text-foreground-800">
              公司 / 组织
            </label>
            <input
              id="company"
              name="company"
              type="text"
              autoComplete="organization"
              value={values.company}
              onChange={(event) => updateField("company", event.target.value)}
              className={inputClass()}
              placeholder="请输入公司或组织名称（选填）"
            />
          </div>
        )}

        <div>
          <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-foreground-800">
            密码 <span className="text-accent-600">*</span>
          </label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete={mode === "login" ? "current-password" : "new-password"}
            value={values.password}
            onChange={(event) => updateField("password", event.target.value)}
            className={inputClass(errors.password)}
            placeholder={mode === "register" ? "至少 6 位字符" : "请输入密码"}
          />
          <FieldError message={errors.password} />
          {mode === "login" && (
            <div className="mt-1.5 text-right">
              <Link
                to="/reset-password"
                className="cursor-pointer text-xs text-foreground-500 transition-colors hover:text-primary-700"
              >
                忘记密码？
              </Link>
            </div>
          )}
        </div>

        {formError && (
          <div className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-3">
            <span className="mt-0.5 flex h-4 w-4 items-center justify-center text-accent-700">
              <i className="ri-error-warning-line" />
            </span>
            <p className="text-xs text-accent-800">{formError}</p>
          </div>
        )}

        {info && (
          <div className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3">
            <span className="mt-0.5 flex h-4 w-4 items-center justify-center text-primary-700">
              <i className="ri-mail-check-line" />
            </span>
            <p className="text-xs text-primary-800">{info}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="mt-1 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? (
            <>
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-loader-4-line animate-spin" />
              </span>
              处理中…
            </>
          ) : (
            <>
              <span className="flex h-4 w-4 items-center justify-center">
                <i className={mode === "login" ? "ri-login-circle-line" : "ri-user-add-line"} />
              </span>
              {mode === "login" ? "登录" : "注册并开始"}
            </>
          )}
        </button>
      </form>

      <p className="mt-5 text-center text-xs text-foreground-500">
        {mode === "login" ? "还没有账号？" : "已经有账号了？"}
        <button
          type="button"
          onClick={() => switchMode(mode === "login" ? "register" : "login")}
          className="ml-1 cursor-pointer font-medium text-primary-700 hover:text-primary-600"
        >
          {mode === "login" ? "立即注册" : "去登录"}
        </button>
      </p>

      <div className="mt-5 border-t border-background-200 pt-4 text-center">
        <Link
          to="/"
          className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-foreground-500 transition-colors hover:text-foreground-950"
        >
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-arrow-left-line" />
          </span>
          返回首页
        </Link>
      </div>
    </div>
  );
}